import { Download } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { PatientListItem } from "@/api/adapters/patients";
import {
	downloadPatientInterventionCsv,
	type InterventionItem,
} from "@/api/adapters/patients";
import { useAuthCanWriteIntervention } from "@/app/store/auth.store";
import { useInterventionPanelStore } from "@/app/store/intervention-panel.store";
import { Select } from "@/components/ui/Select";
import { EmptyTableRow } from "@/components/feedback/EmptyTableRow";
import { TableSkeleton } from "@/components/feedback/TableSkeleton";
import { useIntervention, useNoteMap } from "@/shared/hooks/use-intervention";

import type { ScheduleEditTarget } from "./ScheduleAddModal";

// The two toolbar filters use the SAME values the backend accepts, so the table on screen and the CSV the
// server builds can never disagree about what "최근 3개월" means.
//
// SHCT-599 · SHCT-876 — `NO_SHOW` is the fourth status. It is a filter of its own rather than a shade of
// `SCHEDULED`: 미완료 is a past appointment nobody attended, so folding it into 예정 puts rows that can
// never come round again into a group sorted nearest-first. `MedicalInterventionService.STATUS_FILTERS`
// accepts it, and the CSV export runs through the same `timeline()` call, so the file matches the screen.
type StatusFilter = "ALL" | "SCHEDULED" | "COMPLETED" | "NO_SHOW";
type PeriodFilter = "ALL" | "LAST_1_MONTH" | "LAST_3_MONTHS";
const PAGE_SIZES = [8, 20, 50];

// PRD §4.3 유형 색상 매핑 (rawType 기준): 메시지=노랑 · 전화=파랑(기록·일정 공통) ·
// 외래/대면=녹색 · 재방문=보라. 매핑 밖(알 수 없는 값)은 회색 폴백.
const TYPE_BADGE_CLS: Record<string, string> = {
	MESSAGE: "badge--yellow",
	CALL: "badge--blue",
	VISIT: "badge--green",
	INPERSON: "badge--green",
	FU: "badge--purple",
};

/**
 * Whether a 예정 row may still be opened in the 「일정 정보」 modal.
 *
 * Implements the **「일정 접근 창」 canonical** — PRD 04 v2.26 §3.4 (SHCT-599): the modal stays open
 * until the **다음 날 00:00** of the appointment, and is read-only from that moment (수정·취소·
 * [중재 시작] all closed). `day >= today` is exactly that window: it holds through the whole of the
 * 예정일 and lapses the instant the next day begins.
 *
 * ⚠️ Do NOT narrow this to "미래 예정만". That wording used to sit in §3.4 and v2.26 struck it out —
 * it was never decided policy, only an observation derived from how the list was built. Narrowing
 * kills [중재 시작], because a visit is written up AFTER its time has passed; with the path shut,
 * 「완료 = 링크된 중재 기록」(§4.2) can never hold and every schedule falls to `noshow`. The single
 * cutoff also keeps `noshow` 판정 and edit-access on one time axis instead of two.
 *
 * Status alone cannot express the window: until the server transitions a past appointment out of
 * `scheduled` ([MED.016] `noshow`, D+1), it keeps arriving here as 예정. Slicing the ISO string is
 * also exactly how the row renders the date, so the two can never disagree.
 *
 * A row whose 일시 cannot be read is left clickable — the server keeps such rows deliberately, and
 * withdrawing an action over an unknown is worse than offering one the modal still validates.
 */
/**
 * Is this row a schedule the 「일정 정보」 modal opens for? — any scheduled row, past or not.
 *
 * PRD 04 v2.26 §3.4 「일정 접근 창」 (SHCT-599) corrected the old 「미래 예정만」 reading: the modal
 * opens after the scheduled time too. Whether it opens WRITABLE is a separate question, answered
 * by `isWithinAccessWindow` below.
 */
function isScheduleRow(item: InterventionItem): boolean {
	// EVERY schedule status, named one by one rather than as `!== "completed"` — spelling it as a
	// negation would admit a fourth status silently the day one is added.
	//
	// SHCT-599 — `completed` joined the list once [중재 시작] began writing the link. Before that no
	// appointment could reach 완료, so the case never arose; afterwards a completed appointment landed in
	// the 완료 group as a row that LOOKED like its neighbours and did nothing at all when clicked, because
	// it matched neither this test nor the `kind === "note"` branch. A row carrying the pointer affordance
	// must lead somewhere.
	return (
		item.kind === "sched" &&
		(item.status === "scheduled" ||
			item.status === "noshow" ||
			item.status === "completed")
	);
}

/**
 * Is the schedule still inside its 접근 창? — PRD 04 v2.26 §3.4, SHCT-599.
 *
 * The window runs to **00:00 the day after the scheduled date**, so a schedule stays writable for
 * the whole of its own day and freezes at midnight. That is the same cutoff the `noshow` judgement
 * uses, deliberately: one time axis, read as "you can tidy it up until it is judged".
 *
 * A date the server sends in an unexpected shape is treated as open rather than frozen — locking
 * a row on a parsing failure would take away an action the clinician is entitled to.
 */
function isWithinAccessWindow(item: InterventionItem): boolean {
	const day = item.dt.slice(0, 10);
	if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return true;
	const now = new Date();
	const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
	return day >= today;
}

interface Props {
	patient: PatientListItem;
	/** FE-13: open the schedule modal in edit mode for a future scheduled row. */
	onScheduleEdit?: (target: ScheduleEditTarget) => void;
}

export function InterventionTab({ patient, onScheduleEdit }: Props) {
	const { t } = useTranslation("medical");
	const canWrite = useAuthCanWriteIntervention();

	const STATUS_OPTIONS = [
		{ value: "ALL", label: t("patients.intervention.table.statusFilter.all") },
		{
			value: "SCHEDULED",
			label: t("patients.intervention.table.statusFilter.scheduled"),
		},
		{
			value: "COMPLETED",
			label: t("patients.intervention.table.statusFilter.completed"),
		},
		// 전체 · 예정 · 완료 · 미완료 — PRD 04 §3.4 툴바 (SHCT-599 §5, SHCT-876).
		{
			value: "NO_SHOW",
			label: t("patients.intervention.table.statusFilter.incomplete"),
		},
	];

	const PERIOD_OPTIONS = [
		{ value: "ALL", label: t("patients.intervention.table.periodFilter.all") },
		{
			value: "LAST_1_MONTH",
			label: t("patients.intervention.table.periodFilter.month1"),
		},
		{
			value: "LAST_3_MONTHS",
			label: t("patients.intervention.table.periodFilter.month3"),
		},
	];

	const PAGE_SIZE_OPTIONS = PAGE_SIZES.map((s) => ({
		value: String(s),
		label: t("patients.intervention.table.pageSize", { count: s }),
	}));
	// ── API-MIGRATION ──────────────────────────────────────────────
	// Replace `getInterventionItems` + `useRuntimeNotesStore` with:
	//
	//   const { data: items } = useQuery({
	//     queryKey: ['intervention-items', patient.uid],
	//     queryFn: () => fetchInterventionItems(patient.uid),
	//   });
	//
	// After saving a note (POST /api/notes), invalidate this query
	// so the timeline refreshes automatically:
	//   queryClient.invalidateQueries({ queryKey: ['intervention-items', patient.uid] });
	// ───────────────────────────────────────────────────────────────

	const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
	const [periodFilter, setPeriodFilter] = useState<PeriodFilter>("ALL");
	const [pageSize, setPageSize] = useState(8);
	const [page, setPage] = useState(1);

	const { data: allItems = [], isLoading } = useIntervention(patient, {
		status: statusFilter,
		period: periodFilter,
	});
	const noteMap = useNoteMap(patient.uid);
	const openPanel = useInterventionPanelStore((s) => s.openPanel);

	const handleNoteClick = (item: InterventionItem) => {
		// Completed record → open the read-only viewer panel.
		if (item.kind === "note") {
			const note = noteMap.get(item.id);
			if (note) openPanel(patient, note);
			return;
		}
		// Scheduled row → 「일정 정보」 modal, gated to writers (CRC excluded). SHCT-599: a row past
		// its 접근 창 still opens, read-only — the clinician can read back what the appointment was
		// without being able to change it. Refusing the click would hide the record, not freeze it.
		if (isScheduleRow(item) && canWrite && onScheduleEdit) {
			onScheduleEdit({
				id: item.id,
				scheduledAt: item.dt,
				type: (item.rawType ?? "").toLowerCase(),
				purpose: item.purpose ?? "",
				// SHCT-788 — the memo alone, never `text`: that is 목적 and 메모 already joined for display,
				// and prefilling it here is what made every edit stack another 목적 into the 내용 column.
				memo: item.memo ?? "",
				// SHCT-599 — a COMPLETED appointment is read-only whatever the 접근 창 says, and that is the
				// server's own position rather than a rule invented here: `MedicalScheduleService.edit()`
				// runs the new 예정 일시 through `parseFutureDateTime`, and `cancel()` refuses anything not
				// in the future. A completed appointment is by definition past — the record is written up
				// after the visit — so both writes are guaranteed 400s. Offering [저장] and [일정 취소] on it
				// would put two buttons on screen that cannot succeed. Hiding them also keeps [중재 시작]
				// away (it is gated on `!readOnly`), which matters more: a second record would overwrite
				// `interventionId` and silently detach the first one.
				readOnly: item.status === "completed" || !isWithinAccessWindow(item),
			});
		}
	};

	// Pagination
	const totalPages = Math.max(1, Math.ceil(allItems.length / pageSize));
	const safePage = Math.min(page, totalPages);
	const paged = allItems.slice((safePage - 1) * pageSize, safePage * pageSize);

	return (
		<div>
			{/* ── Filter bar ── */}
			<div className="pro-toolbar justify-start">
				<Select
					value={statusFilter}
					onChange={(v) => {
						setStatusFilter(v as StatusFilter);
						setPage(1);
					}}
					options={STATUS_OPTIONS}
				/>
				<Select
					value={periodFilter}
					onChange={(v) => {
						setPeriodFilter(v as PeriodFilter);
						setPage(1);
					}}
					options={PERIOD_OPTIONS}
				/>
				<div className="ml-auto flex items-center gap-3">
					<Select
						value={String(pageSize)}
						onChange={(v) => {
							setPageSize(Number(v));
							setPage(1);
						}}
						options={PAGE_SIZE_OPTIONS}
					/>
					<button
						type="button"
						className="btn btn--secondary btn--sm"
						onClick={() =>
							downloadPatientInterventionCsv(patient, {
								status: statusFilter,
								period: periodFilter,
							})
						}
					>
						<Download size={14} className="mr-1" />
						CSV
					</button>
				</div>
			</div>

			{/* ── Table ── */}
			<div className="card">
				<div className="card__body card__body--no-padding">
					<table className="intervention-table">
						<thead className="intervention-table__head">
							<tr>
								<th>{t("patients.intervention.table.headers.status")}</th>
								<th>{t("patients.intervention.table.headers.dateTime")}</th>
								<th>{t("patients.intervention.table.headers.staff")}</th>
								<th>{t("patients.intervention.table.headers.type")}</th>
								<th>{t("patients.intervention.table.headers.content")}</th>
							</tr>
						</thead>
						<tbody>
							{isLoading ? (
							<TableSkeleton rows={5} cols={5} />
							) : paged.length === 0 ? (
								<EmptyTableRow
									colSpan={5}
									message={t("patients.intervention.table.empty")}
								/>
							) : (
								paged.map((it) => (
									<InterventionRow
										// SHCT-809/804 — `id` is the bare table id, which a schedule and a note can
										// share, so two rows could carry the same key. React then reused the wrong DOM
										// node every time the list changed (a save, a filter switch), which is what
										// surfaced as duplicated rows and 완료 sitting above 예정 until a reload.
										key={it.rowKey}
										item={it}
										onClick={() => handleNoteClick(it)}
									/>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* ── Pagination ── */}
			{allItems.length > pageSize && (
				<div className="intv-pagination">
					<button
						type="button"
						className="intv-pg__btn"
						disabled={safePage <= 1}
						onClick={() => setPage((p) => Math.max(1, p - 1))}
					>
						{t("patients.intervention.table.pagination.prev")}
					</button>
					{Array.from({ length: totalPages }, (_, i) => i + 1).map((pg) => (
						<button
							key={pg}
							type="button"
							className={`intv-pg__btn${pg === safePage ? " intv-pg__btn--active" : ""}`}
							onClick={() => setPage(pg)}
						>
							{pg}
						</button>
					))}
					<button
						type="button"
						className="intv-pg__btn"
						disabled={safePage >= totalPages}
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
					>
						{t("patients.intervention.table.pagination.next")}
					</button>
				</div>
			)}
		</div>
	);
}

// ── Row component ──

function InterventionRow({
	item,
	onClick,
}: {
	item: InterventionItem;
	onClick?: () => void;
}) {
	const { t } = useTranslation("medical");
	// The row's affordance and its handler read the SAME test — a cursor that promises a modal the click
	// handler then refuses is the shape SHCT-876 was reported as.
	const clickable = item.kind === "note" || isScheduleRow(item);

	// PRD 04 v2.19 §3.4 [MED.016] 상태 배지 — 예정(채움 인디고) / 완료(회색) / 미완료(주의).
	const statusBadge =
		item.status === "completed" ? (
			<span className="intv-status intv-status--done">
				{t("patients.intervention.table.statusFilter.completed")}
			</span>
		) : item.status === "noshow" ? (
			<span className="intv-status intv-status--noshow">
				{t("patients.intervention.table.statusFilter.incomplete")}
			</span>
		) : (
			<span className="intv-status intv-status--scheduled">
				{t("patients.intervention.table.statusFilter.scheduled")}
			</span>
		);

	// PRD §4.3 유형 색상 매핑: 메시지=노랑 · 전화=파랑(기록·일정 공통) · 외래/대면=녹색 · 재방문=보라
	const typeCls = TYPE_BADGE_CLS[item.rawType ?? ""] ?? "badge--gray";

	// MED.001 / PRD §4.1 — a message/call record saved without a title shows the
	// type default label ('메시지 발송' / '전화 상담') instead of an empty title line.
	const defaultTitle =
		item.rawType === "MESSAGE"
			? t("patients.intervention.form.defaultTitle.message")
			: item.rawType === "CALL"
				? t("patients.intervention.form.defaultTitle.call")
				: "";
	const displayTitle = item.title || defaultTitle;

	const roleBadge = item.staffRole ? (
		<span
			className={`badge intv-role-badge ${item.staffRole === "의사" ? "badge--indigo" : "badge--green"} badge--xs`}
		>
			{item.staffRole}
		</span>
	) : null;

	return (
		<tr
			className={`intervention-row${item.kind === "sched" ? " intervention-row--sched" : ""}${
				clickable ? " intervention-row--editable !cursor-pointer" : ""
			}`}
			onClick={clickable ? onClick : undefined}
		>
			<td>{statusBadge}</td>
			<td>
				{item.dt.slice(0, 10).replace(/-/g, ".")}
				<span className="text-gray-500 ml-1">
					{item.dt.slice(11, 16)}
				</span>
			</td>
			<td>
				<span>{item.staff || "-"}</span>
				{roleBadge}
			</td>
			<td>
				<span className={`badge intv-type-badge ${typeCls}`}>
					{item.typeLabel}
				</span>
			</td>
			<td>
				{displayTitle && <div className="intv-row__title">{displayTitle}</div>}
				<div className="intv-row__text">{item.text}</div>
			</td>
		</tr>
	);
}
